import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import {
  merchantReportingApi,
  type MerchantBatchDetailRow,
  type MerchantBatchSummaryRow,
  type MerchantPaymentSummaryRow,
} from '../../lib/api'

interface MerchantReportingSectionProps {
  merchant: {
    id: string
    dba_name: string
    fiserv_mid: string | null
  }
}

type ActiveTab = 'batches' | 'payments'

function defaultFrom(): string {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

function value(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

function dateOnly(value: string): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10)
  return parsed.toLocaleDateString()
}

function errorMessage(error: unknown): string {
  const maybe = error as { response?: { data?: { message?: string; errorMessage?: string } } }
  return maybe.response?.data?.message ?? maybe.response?.data?.errorMessage ?? 'Reporting unavailable. Refresh and try again.'
}

export function MerchantReportingSection({ merchant }: MerchantReportingSectionProps) {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<ActiveTab>('batches')
  const [from, setFrom] = useState(defaultFrom())
  const [to, setTo] = useState(today())
  const [batches, setBatches] = useState<MerchantBatchSummaryRow[] | null>(null)
  const [payments, setPayments] = useState<MerchantPaymentSummaryRow[] | null>(null)
  const [selectedBatch, setSelectedBatch] = useState<MerchantBatchSummaryRow | null>(null)
  const [batchDetail, setBatchDetail] = useState<MerchantBatchDetailRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [voucherError, setVoucherError] = useState<string | null>(null)

  const disabled = !merchant.fiserv_mid
  const dateParams = useMemo(() => ({ from, to, page: 1, per_page: 10 }), [from, to])

  const metricsMutation = useMutation({
    mutationFn: () => merchantReportingApi.syncMetrics(merchant.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['merchant-context', merchant.id] }),
        queryClient.invalidateQueries({ queryKey: ['merchants'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] }),
      ])
    },
    onError: (err) => setError(errorMessage(err)),
  })

  const batchMutation = useMutation({
    mutationFn: () => merchantReportingApi.batchSummary(merchant.id, dateParams),
    onSuccess: (response) => {
      setError(null)
      setBatches(response.data)
      setSelectedBatch(null)
      setBatchDetail(null)
      metricsMutation.mutate()
    },
    onError: (err) => setError(errorMessage(err)),
  })

  const paymentMutation = useMutation({
    mutationFn: () => merchantReportingApi.paymentSummary(merchant.id, dateParams),
    onSuccess: (response) => {
      setError(null)
      setPayments(response.data)
      metricsMutation.mutate()
    },
    onError: (err) => setError(errorMessage(err)),
  })

  const detailMutation = useMutation({
    mutationFn: (batch: MerchantBatchSummaryRow) => merchantReportingApi.batchDetail(merchant.id, batch.batch_number, {
      report_date: batch.report_date.slice(0, 10),
      page: 1,
      per_page: 100,
    }),
    onSuccess: (response) => {
      setError(null)
      setBatchDetail(response.data)
    },
    onError: (err) => setError(errorMessage(err)),
  })

  const voucherMutation = useMutation({
    mutationFn: (row: MerchantBatchDetailRow) => merchantReportingApi.voucher(merchant.id, row.transaction_id, {
      report_date: (row.transaction_date || selectedBatch?.report_date || today()).slice(0, 10),
    }),
    onSuccess: (response) => {
      if (!response.success || !response.pdf) {
        setVoucherError(response.errorMessage ?? 'Fiserv did not return a voucher receipt.')
        return
      }

      setVoucherError(null)
      const url = `data:application/pdf;base64,${response.pdf}`
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    onError: (err) => setVoucherError(errorMessage(err)),
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Fiserv Reporting</CardTitle>
            <p className="mt-1 text-sm text-gray-500">
              Batch summary, batch detail, and deposit history from AccessOne Reporting.
            </p>
          </div>
          <div className="flex rounded-md border border-gray-200 bg-white p-1 text-sm">
            <button
              type="button"
              className={`rounded px-3 py-1.5 font-medium ${tab === 'batches' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
              onClick={() => setTab('batches')}
            >
              Batches
            </button>
            <button
              type="button"
              className={`rounded px-3 py-1.5 font-medium ${tab === 'payments' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
              onClick={() => setTab('payments')}
            >
              Deposits
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {disabled ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Add a Fiserv MID before fetching Reporting data.
          </p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <label className="text-sm font-medium text-gray-700">
                From
                <Input className="mt-1 w-40" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
              </label>
              <label className="text-sm font-medium text-gray-700">
                To
                <Input className="mt-1 w-40" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
              </label>
              <Button
                onClick={() => (tab === 'batches' ? batchMutation.mutate() : paymentMutation.mutate())}
                disabled={batchMutation.isPending || paymentMutation.isPending || metricsMutation.isPending}
              >
                {batchMutation.isPending || paymentMutation.isPending || metricsMutation.isPending ? 'Fetching...' : 'Fetch'}
              </Button>
            </div>

            {error && (
              <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {error}
              </div>
            )}
            {voucherError && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {voucherError}
              </div>
            )}

            {tab === 'batches' ? (
              <BatchTable
                rows={batches}
                selectedBatch={selectedBatch}
                onSelect={(batch) => {
                  setSelectedBatch(batch)
                  setBatchDetail(null)
                  detailMutation.mutate(batch)
                }}
              />
            ) : (
              <PaymentTable rows={payments} />
            )}

            {tab === 'batches' && selectedBatch && (
              <BatchDetailTable
                batch={selectedBatch}
                rows={batchDetail}
                loading={detailMutation.isPending}
                voucherLoading={voucherMutation.isPending}
                onVoucher={(row) => voucherMutation.mutate(row)}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function BatchTable({
  rows,
  selectedBatch,
  onSelect,
}: {
  rows: MerchantBatchSummaryRow[] | null
  selectedBatch: MerchantBatchSummaryRow | null
  onSelect: (row: MerchantBatchSummaryRow) => void
}) {
  if (rows === null) return <p className="py-4 text-center text-sm text-gray-500">Choose a date range and fetch batches.</p>
  if (rows.length === 0) return <p className="py-4 text-center text-sm text-gray-500">No batches found for this date range.</p>

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr className="bg-gray-50">
            {['Report Date', 'Terminal #', 'Batch #', '% Keyed', 'Avg. Trans', '# Trans', 'Sales', 'Returns', 'Net'].map((header) => (
              <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, index) => (
            <tr
              key={`${row.batch_number}-${row.terminal_number}-${index}`}
              className={`cursor-pointer hover:bg-blue-50 ${selectedBatch?.batch_number === row.batch_number ? 'bg-blue-50' : ''}`}
              onClick={() => onSelect(row)}
            >
              <td className="px-3 py-2">{dateOnly(row.report_date)}</td>
              <td className="px-3 py-2 font-mono text-xs">{value(row.terminal_number)}</td>
              <td className="px-3 py-2 font-mono text-xs text-blue-700 underline decoration-dotted">{value(row.batch_number)}</td>
              <td className="px-3 py-2">{row.percent_keyed ?? '—'}%</td>
              <td className="px-3 py-2">{money(row.average_transaction)}</td>
              <td className="px-3 py-2">{value(row.transaction_count)}</td>
              <td className="px-3 py-2">{money(row.sales)}</td>
              <td className="px-3 py-2">{money(row.returns)}</td>
              <td className="px-3 py-2 font-semibold">{money(row.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function BatchDetailTable({
  batch,
  rows,
  loading,
  voucherLoading,
  onVoucher,
}: {
  batch: MerchantBatchSummaryRow
  rows: MerchantBatchDetailRow[] | null
  loading: boolean
  voucherLoading: boolean
  onVoucher: (row: MerchantBatchDetailRow) => void
}) {
  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-semibold text-gray-900">Batch {batch.batch_number} detail</h4>
      {loading && <p className="py-4 text-sm text-gray-500">Loading transactions...</p>}
      {!loading && rows !== null && rows.length === 0 && <p className="py-4 text-sm text-gray-500">No transactions found in this batch.</p>}
      {!loading && rows && rows.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['Date', 'Time', 'Code', 'Keyed', 'Entry', 'Card Type', 'Card #', 'Auth #', 'Amount', 'Voucher'].map((header) => (
                  <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, index) => (
                <tr key={`${row.transaction_id}-${index}`}>
                  <td className="px-3 py-2">{dateOnly(row.transaction_date)}</td>
                  <td className="px-3 py-2">{value(row.transaction_time)}</td>
                  <td className="px-3 py-2">{value(row.transaction_code)}</td>
                  <td className="px-3 py-2">{value(row.keyed)}</td>
                  <td className="px-3 py-2">{value(row.entry_mode)}</td>
                  <td className="px-3 py-2">{value(row.card_type)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{value(row.card_number)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{value(row.authorization_number)}</td>
                  <td className="px-3 py-2 font-semibold">{money(row.transaction_amount)}</td>
                  <td className="px-3 py-2">
                    <Button
                      variant="secondary"
                      onClick={() => onVoucher(row)}
                      disabled={voucherLoading || !row.transaction_id}
                    >
                      Voucher
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function PaymentTable({ rows }: { rows: MerchantPaymentSummaryRow[] | null }) {
  if (rows === null) return <p className="py-4 text-center text-sm text-gray-500">Choose a date range and fetch deposits.</p>
  if (rows.length === 0) return <p className="py-4 text-center text-sm text-gray-500">No deposits found for this date range.</p>

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr className="bg-gray-50">
            {['Report Date', 'Payment Date', 'Acct Last 4', 'Sales', 'Returns', 'Fees', 'Adjustments', 'Net'].map((header) => (
              <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, index) => (
            <tr key={`${row.payment_date}-${row.net}-${index}`}>
              <td className="px-3 py-2">{dateOnly(row.report_date)}</td>
              <td className="px-3 py-2">{dateOnly(row.payment_date)}</td>
              <td className="px-3 py-2 font-mono text-xs">{value(row.account_number_last4)}</td>
              <td className="px-3 py-2">{money(row.sales)}</td>
              <td className="px-3 py-2">{money(row.returns)}</td>
              <td className="px-3 py-2">{money(row.fees)}</td>
              <td className="px-3 py-2">{money(row.adjustments)}</td>
              <td className="px-3 py-2 font-semibold">{money(row.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
